import addOrUpdateRowData from './table-updates/addOrUpdateRowData';
import removeRowData from './table-updates/removeRowData';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { action } from 'satcheljs/lib/legacy';
import { getConversationOrItemIdFromRow } from '../utils/getConversationOrItemIdFromRow';
import { getLastDeliveryTimeStamp } from 'mini-mail-list-store/lib/selectors/mailListDataPropertyGetter';
import { MailListRowDataType } from 'mini-mail-list-store/lib/store/schema/MailListRowDataType';

/**
 * Table operations is responsible for 1) Add, 2) Update 3) Remove of
 * the rows from tableView and the list view store.
 */

/**
 * Adds row in the listView store, tableView itemIds and also
 * updates the currentLoadedIndex
 * @param addAtIndex index where the row needs to be moved
 * @param row to be added
 * @param tableView where item is getting added
 */
export let addRow = action('tableOperations.addRow')(function addRow(
    addAtIndex: number,
    row: MailListRowDataType,
    tableView: TableView
) {
    addOrUpdateRowData(row, tableView);

    addToTableItemIds(tableView, addAtIndex, getConversationOrItemIdFromRow(row));
});

/**
 * Appends row to the end of the list in TableView
 * @param row to be added
 * @param tableView where item is getting added
 */
export let appendRow = action('tableOperations.appendRow')(function appendRow(
    row: MailListRowDataType,
    tableView: TableView
) {
    // Append to the end of table's itemIds list
    const appendIndex = tableView.itemIds.length;
    addRow(appendIndex, row, tableView);
});

/**
 * Removes item in the listview, tableView itemIds
 * and updates the currentLoadedIndex.
 * @param rowId that needs to be removed
 * @param tableView where item is getting removed
 */
export let removeRow = action('tableOperations.removeRow')(function removeRow(
    rowId: string,
    tableView: TableView
) {
    // The order of operations is important as we have to remove the references for rowId first
    // and then remove row data which also takes care of removing the model data row(conversation/item) from the store
    // Remove rowId from table's itemIds list
    removeFromTableItemIds(tableView, rowId);

    // Remove row from list view store
    removeRowData(rowId, tableView);
});

/**
 * Updates row index position in the table in list view store.
 * @param updateIndex index where the row needs to be moved
 * @param rowId row to be updated
 * @param tableView where item is getting updated
 */
export let updateRowPosition = action('tableOperations.updateRowPosition')(
    function updateRowPosition(targetIndex: number, rowId: string, tableView: TableView) {
        const currentIndex = tableView.itemIds.indexOf(rowId);
        if (currentIndex < 0) {
            throw new Error('Row not found in list');
        }

        if (currentIndex === targetIndex) {
            // No updates needed
            return;
        }

        // Remove rowId from table's itemIds list
        removeFromTableItemIds(tableView, rowId);

        // Add to table's itemIds list
        addToTableItemIds(tableView, targetIndex, rowId);
    }
);

/**
 * Updates row row position and data in list view store.
 * @param targetIndex index where the row needs to be moved
 * @param row to be updated
 * @param tableView where item is getting updated
 */
export let updateRow = action('tableOperations.updateRow')(function updateRow(
    targetIndex: number,
    row: MailListRowDataType,
    tableView: TableView
) {
    // Update row position index of row in table
    updateRowPosition(targetIndex, getConversationOrItemIdFromRow(row), tableView);

    // Also update row data
    addOrUpdateRowData(row, tableView);
});

/**
 * Updates row data in list view store.
 * @param row to be updated
 * @param tableView where item is getting updated
 */
export let updateRowData = action('tableOperations.updateRowData')(function updateRowData(
    row: MailListRowDataType,
    tableView: TableView
) {
    // Update row data
    addOrUpdateRowData(row, tableView);
});

/**
 * Clears the table of all rows
 * @param tableView to clear
 * @param skipRowsNewerThanTime - timestamp which is used for skipping deleting new emails
 * @param table row ids to exclude
 */
export let clear = action('tableOperations.clear')(function clear(
    tableView: TableView,
    skipRowsNewerThanTime: Date = null,
    rowIdsToExclude: string[] = []
) {
    for (let i = tableView.itemIds.length - 1; i >= 0; i--) {
        const rowId = tableView.itemIds[i];
        const lastDeliveryTimeStamp = getLastDeliveryTimeStamp(rowId, tableView);

        // Remove all row data if it is not in exclusion list and
        // if no timeStamp is specified or it's older than the timeStamp.
        if (
            rowIdsToExclude.indexOf(tableView.itemIds[i]) === -1 &&
            (!skipRowsNewerThanTime || new Date(lastDeliveryTimeStamp) < skipRowsNewerThanTime)
        ) {
            removeRow(rowId, tableView);
        }
    }
});

/**
 * Returns flag indicating whether the given table already contains the row with the given id
 * @param tableView - Given tableView
 * @param itemId - Given row id
 */
export function containsRow(tableView: TableView, itemId: string): boolean {
    return tableView.itemIds.indexOf(itemId) >= 0;
}

/**
 * Add the given row's Id to the given TableView's itemIds list
 * @param tableView - The table to act on
 * @param addAtIndex - Index to add the rowId into
 * @param rowId - The rowId to add
 */
function addToTableItemIds(tableView: TableView, addAtIndex: number, rowId: string) {
    if (addAtIndex < 0 || addAtIndex > tableView.itemIds.length) {
        throw new Error(
            'Invalid add index ' + addAtIndex + ' with table length ' + tableView.itemIds.length
        );
    }
    if (containsRow(tableView, rowId)) {
        throw new Error('Cannot add itemId to table when it already exists');
    }
    // Adds a row at the specified index
    tableView.itemIds.splice(addAtIndex, 0, rowId);
}

/**
 * Remove the given row Id from the given TableView's itemIds list
 * @param tableView - The table to act on
 * @param rowId - The rowId to add
 */
function removeFromTableItemIds(tableView: TableView, rowId: string) {
    const removeAtIndex = tableView.itemIds.indexOf(rowId);
    if (removeAtIndex < 0) {
        throw new Error('Row not found in list');
    }

    // Remove row at the specified index from item ids
    tableView.itemIds.splice(removeAtIndex, 1);

    // Update current loaded index so that it's not greater than the items we have
    tableView.currentLoadedIndex = Math.min(tableView.currentLoadedIndex, tableView.itemIds.length);
}
